import React, { useState } from 'react';
import { Film, X, AlertCircle, CheckCircle, Save } from 'lucide-react';

const PlaceholderMovieCreator = ({ isVisible, onClose, onAddMovie }) => {
  const [movieForm, setMovieForm] = useState({
    title: '',
    year: '',
    runtime: '',
    director: '',
    plot: '',
    genres: '',
    rating: '',
    color: 'bg-purple-600' // Default color for placeholder movies
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Color options for movie cards
  const colorOptions = [
    { value: 'bg-purple-600', label: 'Purple', preview: 'bg-purple-600' },
    { value: 'bg-blue-600', label: 'Blue', preview: 'bg-blue-600' },
    { value: 'bg-green-600', label: 'Green', preview: 'bg-green-600' },
    { value: 'bg-red-600', label: 'Red', preview: 'bg-red-600' },
    { value: 'bg-orange-600', label: 'Orange', preview: 'bg-orange-600' },
    { value: 'bg-pink-600', label: 'Pink', preview: 'bg-pink-600' },
    { value: 'bg-indigo-600', label: 'Indigo', preview: 'bg-indigo-600' },
    { value: 'bg-gray-600', label: 'Gray', preview: 'bg-gray-600' },
    { value: 'bg-yellow-600', label: 'Yellow', preview: 'bg-yellow-600' },
    { value: 'bg-teal-600', label: 'Teal', preview: 'bg-teal-600' }
  ];

  const handleInputChange = (field, value) => {
    setMovieForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validateForm = () => {
    if (!movieForm.title.trim()) {
      setError('Movie title is required');
      return false;
    }
    
    if (!movieForm.year.trim()) {
      setError('Release year is required');
      return false;
    }
    
    const year = parseInt(movieForm.year);
    if (isNaN(year) || year < 1800 || year > new Date().getFullYear() + 10) {
      setError('Please enter a valid year (1800 - ' + (new Date().getFullYear() + 10) + ')');
      return false;
    }
    
    if (movieForm.runtime.trim()) {
      const runtime = parseInt(movieForm.runtime);
      if (isNaN(runtime) || runtime <= 0 || runtime > 1000) {
        setError('Runtime must be a number between 1 and 1000 minutes');
        return false;
      }
    }
    
    if (movieForm.rating.trim()) {
      const rating = parseFloat(movieForm.rating);
      if (isNaN(rating) || rating < 0 || rating > 10) {
        setError('Rating must be a number between 0 and 10');
        return false;
      }
    }
    
    return true;
  };

  const generatePlaceholderId = () => {
    // Generate a unique ID for placeholder movies
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 5);
    return `placeholder_${timestamp}_${random}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Create placeholder movie object
      const placeholderMovie = {
        id: generatePlaceholderId(),
        title: movieForm.title.trim(),
        year: parseInt(movieForm.year),
        runtime: movieForm.runtime.trim() ? parseInt(movieForm.runtime) : null,
        director: movieForm.director.trim() || null,
        plot: movieForm.plot.trim() || null,
        genres: movieForm.genres.trim() ? movieForm.genres.split(',').map(g => g.trim()).filter(g => g) : [],
        rating: movieForm.rating.trim() ? parseFloat(movieForm.rating) : null,
        color: movieForm.color,
        // Mark as placeholder to distinguish from TMDB movies
        isPlaceholder: true,
        posterUrl: null, // No poster for placeholder movies
        trailerUrl: null, // No trailer for placeholder movies
        tmdbId: null, // No TMDB ID
        // Add timestamp for tracking
        createdAt: new Date().toISOString()
      };
      
      console.log('📝 Created placeholder movie:', placeholderMovie);
      
      // Add to collection
      onAddMovie([placeholderMovie]);
      
      setSuccess(`Successfully created placeholder movie "${placeholderMovie.title}"!`);
      
      // Reset form
      setMovieForm({
        title: '',
        year: '',
        runtime: '',
        director: '',
        plot: '',
        genres: '',
        rating: '',
        color: 'bg-purple-600'
      });
      
      // Auto-close after success
      setTimeout(() => {
        setSuccess('');
        onClose();
      }, 2000);
      
    } catch (err) {
      setError(err.message || 'Failed to create placeholder movie');
      console.error('Placeholder movie creation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setMovieForm({
      title: '',
      year: '',
      runtime: '',
      director: '',
      plot: '',
      genres: '',
      rating: '',
      color: 'bg-purple-600'
    });
    setError('');
    setSuccess('');
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border-2 border-orange-500 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-900 to-orange-900 px-6 py-4 border-b-2 border-orange-500 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Film className="w-6 h-6 text-orange-300" />
            <h2 className="text-xl font-bold text-orange-300">Create Placeholder Movie</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-orange-300 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <p className="text-orange-200 text-sm">
              Create a custom movie entry that doesn't require TMDB data. Perfect for obscure films, 
              personal projects, or movies not in the TMDB database.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Title - Required */}
            <div>
              <label className="block text-orange-300 font-bold mb-2">
                Movie Title <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={movieForm.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter movie title..."
                className="w-full bg-gray-700 border border-orange-500 text-white rounded px-3 py-2 focus:outline-none focus:border-orange-400"
                required
              />
            </div>

            {/* Year - Required */}
            <div>
              <label className="block text-orange-300 font-bold mb-2">
                Release Year <span className="text-red-400">*</span>
              </label>
              <input
                type="number"
                value={movieForm.year}
                onChange={(e) => handleInputChange('year', e.target.value)}
                placeholder="2024"
                min="1800"
                max={new Date().getFullYear() + 10}
                className="w-full bg-gray-700 border border-orange-500 text-white rounded px-3 py-2 focus:outline-none focus:border-orange-400"
                required
              />
            </div>

            {/* Runtime - Optional */}
            <div>
              <label className="block text-orange-300 font-bold mb-2">
                Runtime (minutes)
              </label>
              <input
                type="number"
                value={movieForm.runtime}
                onChange={(e) => handleInputChange('runtime', e.target.value)}
                placeholder="90"
                min="1"
                max="1000"
                className="w-full bg-gray-700 border border-orange-500 text-white rounded px-3 py-2 focus:outline-none focus:border-orange-400"
              />
            </div>

            {/* Director - Optional */}
            <div>
              <label className="block text-orange-300 font-bold mb-2">
                Director
              </label>
              <input
                type="text"
                value={movieForm.director}
                onChange={(e) => handleInputChange('director', e.target.value)}
                placeholder="Director name..."
                className="w-full bg-gray-700 border border-orange-500 text-white rounded px-3 py-2 focus:outline-none focus:border-orange-400"
              />
            </div>

            {/* Genres - Optional */}
            <div>
              <label className="block text-orange-300 font-bold mb-2">
                Genres
              </label>
              <input
                type="text"
                value={movieForm.genres}
                onChange={(e) => handleInputChange('genres', e.target.value)}
                placeholder="Horror, Thriller, Comedy (comma-separated)"
                className="w-full bg-gray-700 border border-orange-500 text-white rounded px-3 py-2 focus:outline-none focus:border-orange-400"
              />
              <p className="text-orange-200 text-xs mt-1">Separate multiple genres with commas</p>
            </div>

            {/* Rating - Optional */}
            <div>
              <label className="block text-orange-300 font-bold mb-2">
                Rating (0-10)
              </label>
              <input
                type="number"
                step="0.1"
                value={movieForm.rating}
                onChange={(e) => handleInputChange('rating', e.target.value)}
                placeholder="7.5"
                min="0"
                max="10"
                className="w-full bg-gray-700 border border-orange-500 text-white rounded px-3 py-2 focus:outline-none focus:border-orange-400"
              />
            </div>

            {/* Plot - Optional */}
            <div>
              <label className="block text-orange-300 font-bold mb-2">
                Plot Summary
              </label>
              <textarea
                value={movieForm.plot}
                onChange={(e) => handleInputChange('plot', e.target.value)}
                placeholder="Brief plot summary..."
                rows="3"
                className="w-full bg-gray-700 border border-orange-500 text-white rounded px-3 py-2 focus:outline-none focus:border-orange-400 resize-vertical"
              />
            </div>

            {/* Color Selection */}
            <div>
              <label className="block text-orange-300 font-bold mb-2">
                Card Color
              </label>
              <div className="grid grid-cols-5 gap-2">
                {colorOptions.map(color => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => handleInputChange('color', color.value)}
                    className={`${color.preview} p-3 rounded text-white text-xs font-bold transition-all ${
                      movieForm.color === color.value 
                        ? 'ring-2 ring-orange-400 ring-offset-2 ring-offset-gray-800' 
                        : 'hover:opacity-80'
                    }`}
                  >
                    {color.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Error/Success Messages */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-900 border border-red-500 rounded text-red-200">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="flex items-center gap-2 p-3 bg-green-900 border border-green-500 rounded text-green-200">
                <CheckCircle size={16} />
                <span>{success}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading || !movieForm.title.trim() || !movieForm.year.trim()}
                className="flex-1 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 text-white px-4 py-3 rounded font-bold transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Create Placeholder Movie
                  </>
                )}
              </button>
              
              <button
                type="button"
                onClick={handleClose}
                className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-3 rounded font-bold transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PlaceholderMovieCreator;











